import React from "react";

const WhatsInItForThemVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson focuses on delivery, not just framing. It shows why the same update lands differently depending on who is listening. Executives want the bottom line fast, operators want the how and what is breaking, finance and product want numbers, and people leaders want the pulse and context. It ends with a simple message formula: outcome, timeline, reason, then the decision you need, so your updates travel further and trigger action.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Now that you know how to frame your message, let's talk about <strong>delivery</strong>, because not everyone listens the same way. One-size-fits-all doesn't really work.</p>

    <p>We'll look at four types of people you'll almost always communicate with:</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">An Executive</h3>
    <p>wants the bottom line first. They don't want the story, they want the summary.</p>
    <p>Say, you're updating your boss, and instead of writing "We've made good progress and are testing features…" try "<strong>Feature ready. Launch by Friday. No blockers.</strong>"</p>
    <p>For them, 4–6 lines on Slack or email, that's gold.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Operators</h3>
    <p>live in the how. They like details, what's working, what's breaking, and what you're doing about it. Give them the A-Z of your project and you'll win their trust.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Finance/Product teams</h3>
    <p>love numbers. If you tell them "Customers are happy," it means nothing. But if you say "<strong>NPS went from 32 to 48 in a month</strong>," they'll listen.</p>
    <p>Their language is data, give them the digits first, context second (or none at all).</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">People leaders</h3>
    <p>prefer conversations. They want to feel the pulse, the mood, the story behind the update. A quick 60-second chat in person, then a short note works best.</p>
    <p>If you say "Hey, the team pulled late nights this week, morale's high. Sharing a short recap below.", that's their rhythm.</p>

    <p>It might take a while to notice the pattern but I'll give you a simple formula you can use to craft your message</p>

    <p><strong>Keep it clean. Three beats, that's all you need.</strong></p>
    <p>"<em>You get X (metric) by Y (time) because of Z (reason). Ask: [approval or decision].</em>"</p>

    <p>For instance to an Exec you could say:</p>
    <p>"<strong>You'll see a 30% faster load time, that's because we cleaned up the backend. Can we roll this out Friday?</strong>"</p>

    <p>See how that works? The moment you frame your update around what's in it for them, you shift the energy. It instantly becomes about their win.</p>

    <p>And when people see their win clearly, decisions happen faster, conversations get smoother, and you become the person everyone wants to work with. And that makes your probability of getting promoted higher.</p>

    <p>Think of every message you send like a wave. If it's all about you, it fades before it reaches the shore. But when it carries what's in it for them, it lands smoothly, right where it needs to.</p>

    <p><strong>People don't care about how far you climbed. They care about how it helps them.</strong></p>

    <p><strong>Have you identified "what's in it for them"?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default WhatsInItForThemVideo;